import {ArrowBack, PlayArrow} from '@mui/icons-material';
import {Box, Button, Chip, Paper, Stack, Typography} from '@mui/material';
import {ExamModule} from '@cp/model';
import {env} from '../../config/env';
import BondTextAttribution from '../../bonds/bondTextAttribution/BondTextAttribution';

interface CourseHeaderProps {
  examModule: ExamModule;
  topicsCount: number;
  categoriesCount: number;
  onBack: () => void;
  onStartSession: () => void;
  isCreatingSession?: boolean;
}

export default function CourseHeader({
  examModule,
  topicsCount,
  categoriesCount,
  onBack,
  onStartSession,
  isCreatingSession = false,
}: CourseHeaderProps) {
  const subjectColor = examModule.subject ? env.subjects[examModule.subject] : env.subjects.MATHEMATICS;
  const levelLabel = examModule.level ? env.levels[examModule.level]?.label || examModule.level : null;

  return (
    <>
      {/* Header with Back Button */}
      <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 3}} data-testid="course-page-header">
        <Button
          startIcon={<ArrowBack />}
          onClick={onBack}
          sx={{minWidth: 'auto'}}
          data-testid="course-page-back-button"
        >
          Back
        </Button>
        <Box sx={{flex: 1}} />
      </Box>

      {/* Module Header Card */}
      <Paper
        sx={{
          p: 4,
          mb: 4,
          background: `linear-gradient(135deg, ${subjectColor.bg} 0%, ${subjectColor.border}15 100%)`,
          border: `2px solid ${subjectColor.border}`,
          borderRadius: 3,
        }}
        data-testid="course-page-module-header"
      >
        <Stack spacing={2}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
            <Typography variant="h2" sx={{fontSize: '3rem'}}>
              {subjectColor.icon}
            </Typography>
            <Box sx={{flex: 1}}>
              <Typography variant="h4" sx={{fontWeight: 700, mb: 1}} data-testid="course-page-module-name">
                {examModule.name}
              </Typography>
              <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1}}>
                {levelLabel && (
                  <Chip
                    label={levelLabel}
                    sx={{
                      bgcolor: subjectColor.border,
                      color: 'white',
                      fontWeight: 600,
                    }}
                    data-testid="course-page-level-chip"
                  />
                )}
                {examModule.examBoard && (
                  <Chip
                    label={examModule.examBoard}
                    sx={{
                      bgcolor: 'white',
                      color: subjectColor.border,
                      border: `2px solid ${subjectColor.border}`,
                      fontWeight: 600,
                    }}
                    data-testid="course-page-exam-board-chip"
                  />
                )}
              </Box>
            </Box>
          </Box>

          <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
            <Typography
              variant="body1"
              sx={{color: 'text.secondary', fontWeight: 500}}
              data-testid="course-page-topics-count"
            >
              {topicsCount} topics • {categoriesCount} categories
            </Typography>
            <Box sx={{flex: 1}} />
            {!examModule.disableQuestion && (
              <Button
                variant="contained"
                size="large"
                startIcon={<PlayArrow />}
                onClick={onStartSession}
                disabled={isCreatingSession}
                sx={{
                  bgcolor: subjectColor.border,
                  color: 'white',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  '&:hover': {
                    bgcolor: subjectColor.border,
                    opacity: 0.9,
                  },
                }}
                data-testid="course-page-start-practice-button"
              >
                Start Practice
              </Button>
            )}
          </Box>

          <BondTextAttribution examModule={examModule} />
        </Stack>
      </Paper>
    </>
  );
}
